import axios from "axios";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  FormControl,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  PopperFullWidth,
  getInputLabelProps,
  getInputProps,
} from "../../../common/appStyles";

import localize from "../../../common/localize";
import ExecutionStatusField from "../../../components/common/ExecutionStatusField";
import {
  GeneralStatusEnum,
  GetDateInYYYYMMDDWithoutTimeFormat,
  getEventCode,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";

export default function PaymentToTechnician({
  eventData,
  paymentData,
  setPaymentData,
}) {
  const { config, PAYMENT_TYPE_ENDPOINT, PERSONAL_API } = useGlobalContext();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const maximumCharactersForObservations = 1000;
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [personalData, setPersonalData] = useState([]);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}`, requestConfig);

    setPersonalData(data?.data?.Data);
  };

  const getPaymentTypes = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PAYMENT_TYPE_ENDPOINT}`, requestConfig);

    setPaymentTypes(data?.data?.Data);
  };

  const handlePersonalChange = (e) => {
    if (!e) {
      setPaymentData({
        ...paymentData,
        PersonalId: eventData?.PersonalAssigned.id,
        PersonalData: eventData?.PersonalAssigned,
      });
      return;
    }

    setPaymentData({
      ...paymentData,
      PersonalId: e.id,
      PersonalData: { ...e },
    });
  };

  const handlePaymentTypeChange = (e) => {
    if (!e) {
      setPaymentData({
        ...paymentData,
        PaymentTypeData: undefined,
        PaymentTypeId: 0,
        Value: 0,
      });
      return;
    }

    setPaymentData({
      ...paymentData,
      PaymentTypeData: e,
      PaymentTypeId: e.Id ?? 0,
      Value:
        e.Type === 1
          ? (eventData.executionFinalValue * e.DefaultValue) / 100
          : e.DefaultValue ?? 0,
    });
  };

  const handlePaymentValueChange = (e) => {
    if (e.target.value) {
      setPaymentData({
        ...paymentData,
        Value: Number.parseFloat(e.target.value),
      });
      return;
    }

    setPaymentData({
      ...paymentData,
      Value: 0,
    });
  };

  const handlePaymentDateChange = (paymentDateValue) => {
    try {
      if (!paymentDateValue) {
        setPaymentData({
          ...paymentData,
          PaymentDate: GetDateInYYYYMMDDWithoutTimeFormat(new Date()),
        });
        return;
      }

      setPaymentData({
        ...paymentData,
        PaymentDate: GetDateInYYYYMMDDWithoutTimeFormat(paymentDateValue),
      });
    } catch {
      console.log("Invalid payment date");
    }
  };

  const handleObservationChange = (value) => {
    setPaymentData({
      ...paymentData,
      Observation: value.target.value,
    });
  };

  useEffect(() => {
    getPersonal();
    getPaymentTypes();
  }, []);

  useEffect(() => {
    setDescriptionCount(paymentData.Observation.length);
  }, [paymentData?.Observation]);

  const mustBeDisabled = () => !paymentTypes || paymentTypes.length === 0;

  const personalSelectorMustBeDisabled = () =>
    paymentData?.Id > 0 || !personalData || personalData.length === 0;

  return (
    <div className="w-full">
      <form className="w-full mt-2">
        <div className="w-full flex flex-col gap-2">
          <div className="w-full">
            <ExecutionStatusField executionStatus={eventData?.EventType} />
          </div>

          <div className="flex">
            <div className="two_columns_grid">
              <label className="field_title_text">
                <b>{`${localize.event}: `}</b>
              </label>
              <label className="field_title_text">{`${eventData?.Id}`}</label>
              {eventData?.ProceedingInfo?.Name && (
                <label className="field_title_text">
                  <b>{localize.proceeding}:</b>
                </label>
              )}
              {eventData?.ProceedingInfo?.Name && (
                <label className="field_title_text">
                  {`${eventData?.ProceedingInfo?.Name ?? localize.no_data}`}
                </label>
              )}

              <label className="field_title_text">
                <b>{localize.code}:</b>{" "}
              </label>
              <label className="field_title_text">
                {!eventData ? localize.no_data : getEventCode(eventData)}
              </label>

              <label className="field_title_text">
                <b>{localize.workflow}: </b>
              </label>
              <label className="field_title_text">
                {eventData?.EventType?.WorkFlow
                  ? eventData?.EventType?.WorkFlow?.Name
                  : localize.no_data}
              </label>

              {paymentData?.Id > 0 && (
                <label className="field_title_text">
                  <b>{localize.personal}: </b>
                </label>
              )}

              {paymentData?.Id > 0 && (
                <label className="field_title_text">
                  {`${paymentData?.PersonalData.Identification} - ${paymentData?.PersonalData.SurName} ${paymentData?.PersonalData.LastName}`}
                </label>
              )}

              {paymentData?.Id > 0 && (
                <label className="field_title_text">
                  <b>{localize.payment_type}: </b>
                </label>
              )}

              {paymentData?.Id > 0 && (
                <label className="field_title_text">
                  {paymentData?.PaymentTypeData?.Name}
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="flex-col flex-wrap mt-2 mb-6 gap-4">
          {!paymentData?.Id && (
            <Combobox
              isRequired
              fullwidth
              id="personal"
              fieldLabel={localize.personal}
              fieldOptions={personalData ?? []}
              fieldValue={paymentData?.PersonalData}
              handleValueChange={handlePersonalChange}
              customOptionLabel={(option) =>
                `${option.Identification} - ${option.SurName} ${option.LastName}`
              }
              popperFullWidth={PopperFullWidth}
              disabled={personalSelectorMustBeDisabled()}
            />
          )}

          {!paymentData?.Id && (
            <div className="my-4">
              <Combobox
                isRequired
                fullwidth
                id="paymentType"
                fieldLabel={localize.payment_type}
                fieldOptions={paymentTypes ?? []}
                fieldValue={paymentData?.PaymentTypeData}
                handleValueChange={handlePaymentTypeChange}
                popperFullWidth={PopperFullWidth}
                disabled={paymentData?.Id > 0 || mustBeDisabled()}
              />
            </div>
          )}

          <div className="my-4">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={mustBeDisabled()}
                label={localize.payment_date}
                className="w-full"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                PopperComponent={PopperFullWidth}
                value={paymentData.PaymentDate}
                onChange={handlePaymentDateChange}
                maxDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: "",
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: {
                        fontSize: "1em",
                        paddingBottom: "10px",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </div>

          <TextField
            className="my-4"
            disabled={mustBeDisabled()}
            type="number"
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              ...getInputProps(),
              inputProps: {
                min: 0,
              },
            }}
            value={paymentData?.Value}
            onChange={handlePaymentValueChange}
            size="small"
            id="paymentValue"
            name="paymentValue"
            label={localize.service_value}
            variant="outlined"
            fullWidth
          />

          <div className="w-full my-4">
            {paymentData?.Id > 0 ? (
              paymentData?.Observation && (
                <Alert className="w-full text_little_text" severity="info">
                  <span className="text-xs">
                    <b>{localize.observations}:</b> {paymentData.Observation}
                  </span>
                </Alert>
              )
            ) : (
              <FormControl required fullWidth>
                <TextField
                  disabled={mustBeDisabled()}
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    style: {
                      fontSize: "0.8em",
                    },
                    maxLength: maximumCharactersForObservations,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  id="outlined-basic"
                  name="Observation"
                  label={localize.observations}
                  variant="outlined"
                  onChange={handleObservationChange}
                  value={paymentData.Observation}
                  helperText={`${descriptionCount}/${maximumCharactersForObservations}`}
                />
              </FormControl>
            )}
          </div>

          {paymentData?.Id > 0 && (
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Card
                variant="outlined"
                onClick={() =>
                  setPaymentData({
                    ...paymentData,
                    Status: GeneralStatusEnum.Approved,
                  })
                }
                sx={{
                  flex: 1,
                  border:
                    paymentData?.Status === GeneralStatusEnum.Approved
                      ? "2px solid #2E7D32"
                      : "1px solid #E0E0E0",
                  backgroundColor:
                    paymentData?.Status === GeneralStatusEnum.Approved
                      ? "#E8F5E9"
                      : "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
              >
                <CardActionArea
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Radio
                    size="small"
                    checked={paymentData?.Status === GeneralStatusEnum.Approved}
                    sx={{
                      color: "#2E7D32",
                      "&.Mui-checked": { color: "#2E7D32" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#2E7D32",
                    }}
                  >
                    {localize.approve}
                  </Typography>
                </CardActionArea>
              </Card>

              <Card
                variant="outlined"
                onClick={() =>
                  setPaymentData({
                    ...paymentData,
                    Status: GeneralStatusEnum.Rejected,
                  })
                }
                sx={{
                  flex: 1,
                  border:
                    paymentData?.Status === GeneralStatusEnum.Rejected
                      ? "2px solid #C62828"
                      : "1px solid #E0E0E0",
                  backgroundColor:
                    paymentData?.Status === GeneralStatusEnum.Rejected
                      ? "#FFEBEE"
                      : "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
              >
                <CardActionArea
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Radio
                    size="small"
                    checked={paymentData?.Status === GeneralStatusEnum.Rejected}
                    sx={{
                      color: "#C62828",
                      "&.Mui-checked": { color: "#C62828" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#C62828",
                    }}
                  >
                    {localize.reject}
                  </Typography>
                </CardActionArea>
              </Card>
            </Box>
          )}

          {paymentData?.Id > 0 && (
            <div className="flex flex-wrap mb-6 mt-3">
              <FormControl required fullWidth>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    style: {
                      fontSize: "0.8em",
                    },
                    maxLength: maximumCharactersForDescription,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  name="Observation"
                  label={localize.observations}
                  variant="outlined"
                  onChange={(e) => {
                    setPaymentData({
                      ...paymentData,
                      StatusObservation: e.target.value,
                    });
                  }}
                  value={paymentData?.StatusObservation}
                  helperText={
                    paymentData?.Status === GeneralStatusEnum.Rejected &&
                    (!paymentData?.StatusObservation ||
                      paymentData?.StatusObservation?.length === 0)
                      ? localize.error_empty_field
                      : `${
                          paymentData?.StatusObservation?.length ?? 0
                        }/${maximumCharactersForDescription}`
                  }
                  error={
                    paymentData?.Status === GeneralStatusEnum.Rejected &&
                    (!paymentData?.StatusObservation ||
                      paymentData?.StatusObservation?.length === 0)
                  }
                />
              </FormControl>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
